/**
 * Utilitaire pour les appels fetch avec authentification JWT
 * Compatible avec le streaming SSE (Server-Sent Events)
 * Gère automatiquement le refresh des tokens sur 401
 */

import logger from './logger';

/**
 * Effectue un appel fetch avec authentification JWT
 * @param {string} url - URL de l'endpoint (relative ou absolue)
 * @param {Object} options - Options fetch standard
 * @param {boolean} skipAuthRefresh - Si true, ne tente pas de refresh le token sur 401
 * @returns {Promise<Response>} - Réponse fetch
 */
export const fetchWithAuth = async (url, options = {}, skipAuthRefresh = false) => {
  const token = localStorage.getItem('accessToken');

  // Préparer les headers avec le token JWT
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Effectuer la requête
  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Si 401 et qu'on n'est pas déjà en train de refresh, tenter le refresh
  if (response.status === 401 && !skipAuthRefresh) {
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      try {
        // Tenter de refresh le token
        const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL_AUTHENT}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          localStorage.setItem('accessToken', data.access_token);

          // Réessayer la requête originale avec le nouveau token
          return fetchWithAuth(url, options, true); // skipAuthRefresh=true pour éviter la boucle infinie
        } else {
          // Le refresh a échoué, déconnecter l'utilisateur
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      } catch (error) {
        logger.error('Erreur lors du refresh du token:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    } else {
      // Pas de refresh token, rediriger vers login
      window.location.href = '/login';
    }
  }

  return response;
};

/**
 * Helper pour les requêtes GET avec authentification
 */
export const getWithAuth = (url, options = {}) => {
  return fetchWithAuth(url, { ...options, method: 'GET' });
};

/**
 * Helper pour les requêtes POST avec authentification
 */
export const postWithAuth = (url, body, options = {}) => {
  return fetchWithAuth(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Helper pour les requêtes PUT avec authentification
 */
export const putWithAuth = (url, body, options = {}) => {
  return fetchWithAuth(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/**
 * Helper pour les requêtes DELETE avec authentification
 */
export const deleteWithAuth = (url, options = {}) => {
  return fetchWithAuth(url, { ...options, method: 'DELETE' });
};

/**
 * Récupère les headers d'authentification pour les appels manuels
 * Utile pour axios ou fetch personnalisé
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
